import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Building2,
  CheckCircle2,
  Mail,
  Phone,
  ArrowRight,
  Home,
  Send,
  Star,
  Rocket
} from 'lucide-react';
import './HomePage.css'; // Reutilizamos el mismo CSS

const API_URL = process.env.REACT_APP_API_URL;

const RegisterContactPage = () => {

  // Estados para el formulario simplificado
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    telefono: '',
    email: '',
    cargo: '',
    tipo: '',
    observaciones: '',
    website: '' // honeypot — humanos no lo ven, bots lo rellenan
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Captcha propio (aritmético)
  const [captcha, setCaptcha] = useState({ id: '', question: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const loadCaptcha = async () => {
    try {
      setCaptchaLoading(true);
      setCaptchaAnswer('');
      const resp = await fetch(`${API_URL}/api/auth/captcha`);
      const data = await resp.json();
      setCaptcha({ id: data.id, question: data.question });
    } catch {
      setCaptcha({ id: '', question: 'No se pudo cargar la verificación. Recarga la página.' });
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    loadCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opciones para los selects
  const tiposRegistro = [
    { value: '', label: 'Seleccione una opción' },
    { value: 'cliente', label: 'Cliente - Busco servicios de salud' },
    { value: 'proveedor', label: 'Proveedor - Ofrezco servicios de salud' }
  ];

  // Manejar cambios en los campos
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Limpiar errores al escribir
  };

  // Validar formulario
  const validateForm = () => {
    const { nombre, email, telefono, empresa, tipo } = formData;
    
    if (!nombre.trim()) {
      setError('El nombre es obligatorio');
      return false;
    }
    if (!email.trim()) {
      setError('El email es obligatorio');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('El formato del email no es válido');
      return false;
    }
    if (!telefono.trim()) {
      setError('El teléfono es obligatorio');
      return false;
    }
    if (!empresa.trim()) {
      setError('El nombre de la empresa es obligatorio');
      return false;
    }
    if (!captcha.id) {
      setError('Verificación no disponible. Recarga la página.');
      return false;
    }
    if (!String(captchaAnswer).trim()) {
      setError('Resuelve la operación de verificación');
      return false;
    }

    setError('');
    return true;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/contact-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaId: captcha.id,
          captchaAnswer: String(captchaAnswer).trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Si falló el captcha, pedimos uno nuevo (el anterior ya se consumió en backend)
        await loadCaptcha();
        throw new Error(data.message || 'Error al enviar la solicitud');
      }

      setSuccess(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Página de confirmación mejorada
  if (success) {
    return (
      <div className="demo-form-wrapper" style={{
        background: 'white',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
        border: '1px solid var(--gray-200)'
      }}>
        <div style={{ 
          textAlign: 'center',
          padding: '40px 32px',
          background: 'linear-gradient(135deg, var(--accent-mint), rgba(60, 191, 154, 0.05))'
        }}>
          {/* Icono de éxito animado */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '20px'
          }}>
            <div style={{
              backgroundColor: 'var(--primary-green)',
              borderRadius: '50%',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse-glow 2s ease-in-out infinite'
            }}>
              <CheckCircle2 size={40} color="white" />
            </div>
          </div>

          <h3 style={{ 
            color: 'var(--primary-green)', 
            marginBottom: '12px',
            fontSize: '1.6rem',
            fontWeight: '700'
          }}>
            ¡Solicitud Enviada!
          </h3>

          <p style={{ 
            fontSize: '1rem', 
            lineHeight: '1.5', 
            marginBottom: '24px', 
            color: 'var(--text-secondary)',
            maxWidth: '400px',
            margin: '0 auto 24px auto'
          }}>
            Gracias por tu interés en <strong>CentralSalud</strong>. 
            Nuestro equipo se pondrá en contacto contigo muy pronto.
          </p>

          {/* Información de contacto directo */}
          <div style={{
            background: 'rgba(60, 191, 154, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            border: '1px solid rgba(60, 191, 154, 0.2)'
          }}>
            <p style={{ margin: '0 0 8px 0', color: 'var(--primary-green)', fontWeight: '600', fontSize: '0.9rem' }}>
              ¿Necesitas contactarnos directamente?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={14} color="var(--primary-green)" />
                <span style={{ color: 'var(--text-primary)' }}>soporte@centralsalud.es</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={14} color="var(--primary-green)" />
                <span style={{ color: 'var(--text-primary)' }}>+34 635 82 35 92</span>
              </div>
            </div>
          </div>

          {/* Botón para enviar otra solicitud */}
          <button 
            onClick={() => {
              setSuccess(false);
              setFormData({
                nombre: '', empresa: '', telefono: '', email: '',
                cargo: '', tipo: '', observaciones: '', website: ''
              });
              setCaptchaAnswer('');
              loadCaptcha();
            }}
            style={{
              background: 'linear-gradient(135deg, var(--primary-green), var(--secondary-teal))',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <ArrowRight size={16} />
            Enviar Otra Solicitud
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-form-wrapper" style={{
      background: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
      border: '1px solid var(--gray-200)'
    }}>
      
      {/* Header del formulario */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary-green), var(--secondary-teal))',
        padding: '16px 24px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            padding: '8px',
            backdropFilter: 'blur(10px)'
          }}>
            <Rocket size={20} />
          </div>
        </div>
        <h3 style={{ 
          margin: 0, 
          fontSize: '1.2rem', 
          fontWeight: '700'
        }}>
          Solicitar Demo Gratuita
        </h3>
      </div>

      {/* Contenido del formulario */}
      <div style={{ padding: '32px' }}>
        {error && (
          <div style={{ 
            color: 'var(--error)', 
            marginBottom: '20px', 
            padding: '12px 16px', 
            backgroundColor: 'rgba(245, 101, 101, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(245, 101, 101, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.9rem'
          }}>
            <span>⚠️</span>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* Grid del formulario */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '20px',
            marginBottom: '24px'
          }}>
            
            {/* Nombre completo */}
            <div className="form-group">
              <label htmlFor="nombre" style={{
                display: 'block',
                marginBottom: '6px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                fontSize: '0.9rem'
              }}>
                Nombre Completo *
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                placeholder="Ej: Juan Pérez"
                value={formData.nombre}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid var(--gray-200)',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-green)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
              />
            </div>

            {/* Teléfono */}
            <div className="form-group">
              <label htmlFor="telefono" style={{
                display: 'block',
                marginBottom: '6px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                fontSize: '0.9rem'
              }}>
                Teléfono *
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                placeholder="+34 600 123 456"
                value={formData.telefono}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid var(--gray-200)',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-green)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
              />
            </div>
            
            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" style={{
                display: 'block',
                marginBottom: '6px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                fontSize: '0.9rem'
              }}>
                Correo Electrónico *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="ejemplo@empresa.com"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{
                  width: '210%',
                  padding: '12px 16px',
                  border: '2px solid var(--gray-200)',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-green)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
              />
            </div>
            
          </div>

          {/* Honeypot: oculto visual y semánticamente — humanos no lo ven, bots lo rellenan */}
          <div aria-hidden="true" style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}>
            <label htmlFor="website">No rellenar</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website}
              onChange={handleInputChange}
            />
          </div>

          {/* Empresa - campo completo */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="empresa" style={{
              display: 'block',
              marginBottom: '6px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              fontSize: '0.9rem'
            }}>
              Nombre de la Empresa *
            </label>
            <input
              type="text"
              id="empresa"
              name="empresa"
              placeholder="Ej: Clínica San Juan"
              value={formData.empresa}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid var(--gray-200)',
                borderRadius: '8px',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
                background: 'white'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary-green)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
            />
          </div>

          {/* Verificación anti-spam (captcha propio) */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="captchaAnswer" style={{
              display: 'block',
              marginBottom: '6px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              fontSize: '0.9rem'
            }}>
              Verificación *
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                padding: '12px 16px',
                background: 'var(--gray-100, #f3f4f6)',
                border: '2px solid var(--gray-200)',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                minWidth: '180px',
                color: 'var(--text-primary)'
              }}>
                {captchaLoading ? 'Cargando...' : (captcha.question || '—')}
              </div>
              <input
                type="text"
                id="captchaAnswer"
                name="captchaAnswer"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Tu respuesta"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                required
                style={{
                  flex: 1,
                  minWidth: '140px',
                  padding: '12px 16px',
                  border: '2px solid var(--gray-200)',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  background: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-green)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
              />
              <button
                type="button"
                onClick={loadCaptcha}
                disabled={captchaLoading}
                title="Generar otra operación"
                style={{
                  background: 'transparent',
                  border: '2px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  cursor: captchaLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)'
                }}
              >
                ↻
              </button>
            </div>
          </div>

          {/* Botón de envío */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? 'var(--gray-200)' : 'linear-gradient(135deg, var(--primary-green), var(--secondary-teal))',
              color: loading ? 'var(--text-light)' : 'white',
              border: 'none',
              padding: '16px 24px',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: loading ? 'none' : '0 4px 20px rgba(60, 191, 154, 0.3)'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 30px rgba(60, 191, 154, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(60, 191, 154, 0.3)';
              }
            }}
          >
            {loading ? (
              <>
                <div style={{
                  width: '18px',
                  height: '18px',
                  border: '2px solid transparent',
                  borderTop: '2px solid var(--text-light)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Enviando Solicitud...
              </>
            ) : (
              <>
                <Send size={18} />
                Solicitar Demo Gratuita
              </>
            )}
          </button>

        </form>
      </div>

      {/* CSS para animaciones */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(60, 191, 154, 0.3);
          }
          50% { 
            box-shadow: 0 0 30px rgba(60, 191, 154, 0.5);
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterContactPage;